import InfoOutlinedIcon from '@mui/icons-material/Info';
import { Alert, Autocomplete, AutocompleteRenderInputParams, Box, Grid, SelectChangeEvent, TextField, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { CouponGiftDTO, CouponGiftDTOGiftTypeEnum, CouponGiftDTOOccasionEnum } from 'probonio-shared-ui/api';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../../component/dialog';
import { DialogLoadingButton } from '../../../../component/dialog/DialogLoadingButton';
import { DatePickerControl, SelectControl } from '../../../../component/form';
import { CheckboxControl } from '../../../../component/form/CheckboxControl';
import PlaceholderInput from '../../../../component/form/PlaceholderInput/PlaceholderInput';
import { Placeholder } from '../../../../component/form/PlaceholderInput/PlaceholderInput.types';
import { AvailableCouponsAccordion } from '../../coupons/AvailableCouponsDrawer';
import {
  giftYearlyAvailable,
  INCENTIVE_BUDGET_OPTIONS,
  INCENTIVE_OCCASION_OPTIONS,
  MAX_YEARLY_INCENTIVE_AMOUNT,
} from '../../coupons/giftUtils';
import { useRefetchCouponGifts, useRefetchGiftOccasions } from '../../query';

export interface GiftIncentiveFormValues {
  payoutDate: Date;
  occasion: CouponGiftDTOOccasionEnum;
  description: string;
  amount: number;
  recurring: boolean;
  inviteWhenActive: boolean;
  sendMailWhenActive: boolean;
}

interface Props {
  dialogState: DialogState;
  employeeId: string;
  incentives: CouponGiftDTO[];
  currentGift?: CouponGiftDTO;
  showInviteUser?: boolean;
}

export const GiftsIncentiveDialog: React.FC<Props> = ({ dialogState, employeeId, currentGift, showInviteUser, incentives }) => {
  const { t, i18n } = useTranslation('benefitsModule');
  const { control, handleSubmit, getValues, reset, setValue } = useForm<GiftIncentiveFormValues>();
  const [descriptionPrefill, setDescriptionPrefill] = useState('');
  const getTenantId = useTenantID();
  const withMessage = useWithMessage((err: Error) => ((err as AxiosError).response?.status === 409 ? t('conflictError') : undefined));
  const { enqueueSnackbar } = useSnackbar();

  const refetchOccasions = useRefetchGiftOccasions();
  const refetchGifts = useRefetchCouponGifts(employeeId);

  const { data, isLoading } = useTenantQuery(['benefits', 'coupons', 'definition', apis.coupons.findCouponDefinitions.name], tenantId =>
    apis.coupons.findCouponDefinitions({ tenantId, sortBy: ['name:asc'] }).then(resp => resp.data),
  );
  const { data: occasions } = useTenantQuery(['benefits', 'coupons', 'giftOccasions'], tenantId =>
    apis.coupons.getGiftOccasions({ tenantId }).then(resp => resp.data),
  );
  const amount = useWatch({ control, name: 'amount' });
  const occasion = useWatch({ control, name: 'occasion' });
  const payoutDate = useWatch({ control, name: 'payoutDate' });

  const yearlyIncentivesAmount = incentives
    .filter(
      incentive =>
        incentive.id !== currentGift?.id && DateTime.fromISO(incentive.payoutDate).hasSame(DateTime.fromJSDate(payoutDate), 'year'),
    )
    .reduce((acc, incentive) => acc + incentive.amount, 0);

  const getAmountOptionsLabel = useCallback((value: number) => i18n.format(value, 'euro-float'), [i18n]);

  const renderAmountInput = useCallback(
    (params: AutocompleteRenderInputParams) => <TextField {...params} margin="dense" label={t('INCENTIVES.fields.amount')} />,
    [t],
  );

  const filterAmountOptions = useCallback((options: number[], { inputValue }: { inputValue: string }): number[] => {
    return options.filter(option => `${option / 100}`.includes(inputValue));
  }, []);

  const getDefaultDescriptionForOccasion = useCallback(
    (occasion: CouponGiftDTOOccasionEnum) => {
      const description = occasions?.giftOccasions
        .filter(giftOccasion => giftOccasion.occasion === occasion)
        .map(giftOccasion => giftOccasion.description)[0];
      return description ? description : t(`couponsModule:giftOccasions.description.${occasion}`);
    },
    [occasions?.giftOccasions, t],
  );

  //set values for Form
  useEffect(() => {
    if (dialogState.isOpen) {
      const occasion = currentGift?.occasion || CouponGiftDTOOccasionEnum.Commitment;
      const payoutDate = currentGift?.payoutDate
        ? DateTime.fromISO(currentGift?.payoutDate).toJSDate()
        : DateTime.now().endOf('day').toJSDate();

      const description = currentGift?.description || getDefaultDescriptionForOccasion(occasion);
      reset({
        payoutDate,
        amount: currentGift?.amount || 100_00,
        occasion,
        description,
        recurring: currentGift?.recurring || false,
        inviteWhenActive: currentGift?.inviteWhenActive || false,
        sendMailWhenActive: currentGift?.sendMailWhenActive || false,
      });
      setDescriptionPrefill(description);
    }
  }, [currentGift, dialogState.isOpen, reset, t, getDefaultDescriptionForOccasion]);

  const handleChangeIncentive = useCallback(
    (event: SelectChangeEvent<CouponGiftDTOOccasionEnum>) => {
      const description = getDefaultDescriptionForOccasion(event.target.value as CouponGiftDTOOccasionEnum);
      setValue('description', description);
      setDescriptionPrefill(description);
    },
    [getDefaultDescriptionForOccasion, setValue],
  );

  const giftMutation = useMutation({
    mutationFn: async (gift: GiftIncentiveFormValues) => {
      if (currentGift) {
        const newPayoutDate = DateTime.fromJSDate(gift.payoutDate).toFormat('yyyy-MM-dd');
        await apis.coupons.updateCouponGift({
          couponGiftId: currentGift.id,
          tenantId: getTenantId(),
          employeeId,
          updateCouponGiftDTO: {
            ...gift,
            payoutDate: currentGift.payoutDate.substring(0, 10) === newPayoutDate ? undefined : newPayoutDate,
            recurring: gift.recurring && giftYearlyAvailable(gift.occasion),
          },
        });
      } else {
        await apis.coupons.createCouponGift({
          tenantId: getTenantId(),
          employeeId,
          createCouponGiftDTO: {
            ...gift,
            payoutDate: DateTime.fromJSDate(gift.payoutDate).toFormat('yyyy-MM-dd'),
            recurring: gift.recurring && giftYearlyAvailable(gift.occasion),
            giftType: CouponGiftDTOGiftTypeEnum.Incentive,
          },
        });
      }
    },

    onSuccess: () => {
      void refetchGifts();
      void refetchOccasions();
      enqueueSnackbar(t(currentGift ? 'successUpdate' : 'successCreate'), { variant: 'success' });
      dialogState.handleClose();
    },
    onError: async err => {
      if (currentGift && err instanceof AxiosError && err.response?.status === 422) {
        enqueueSnackbar(t('couponsModule:giftAlreadyCompleted'), { variant: 'warning' });
        dialogState.handleClose();
        await refetchGifts();
      } else {
        withMessage(err as Error);
      }
    },
  });

  const handleAmountChange = useCallback(
    (event: SyntheticEvent, value: number) => {
      setValue('amount', value);
    },
    [setValue],
  );

  const handleSaveGift = useCallback(() => {
    const values = getValues();
    giftMutation.mutate(values);
  }, [getValues, giftMutation]);

  const handleChangeDescription = useCallback(
    (v: string) => {
      setValue('description', v);
    },
    [setValue],
  );

  const placeholders: Placeholder[] = useMemo(
    () => [
      {
        label: t('INCENTIVES.placeHolderInput.firstNameChipLabel'),
        value: 'firstName',
      },
      {
        label: t('INCENTIVES.placeHolderInput.lastNameChipLabel'),
        value: 'lastName',
      },
    ],
    [t],
  );

  return (
    <BasicDialog
      dialogState={dialogState}
      sx={{ overflow: 'visible' }}
      title={currentGift ? t('INCENTIVES.dialogEditTitle') : t('INCENTIVES.dialogCreateTitle')}
      content={
        <form onSubmit={handleSubmit(handleSaveGift)}>
          {occasion === CouponGiftDTOOccasionEnum.Other && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(`couponsModule:giftOccasions.info.${occasion}`)}
            </Alert>
          )}
          {!!yearlyIncentivesAmount && (
            <Alert severity={yearlyIncentivesAmount + amount > MAX_YEARLY_INCENTIVE_AMOUNT ? 'warning' : 'info'} sx={{ mb: 2 }}>
              <strong>
                {t('INCENTIVES.remainingAmountAvailable', {
                  money: i18n.format(MAX_YEARLY_INCENTIVE_AMOUNT - yearlyIncentivesAmount, 'euro'),
                })}
              </strong>
              {yearlyIncentivesAmount + amount > MAX_YEARLY_INCENTIVE_AMOUNT && (
                <>
                  <br />
                  {t('INCENTIVES.maxIncentiveAmountWarning')}
                </>
              )}
            </Alert>
          )}
          <Grid
            container
            wrap="nowrap"
            sx={{
              gap: 1,
              mb: 2,
            }}
          >
            <Grid size={4}>
              <DatePickerControl
                dateFormat="dd.MM.yyyy"
                minDate={DateTime.now().startOf('day').toJSDate()}
                maxDate={DateTime.now().plus({ years: 10 }).toJSDate()}
                control={control}
                name="payoutDate"
                label={t('fields.date')}
                fullWidth
                margin="dense"
                rules={{
                  required: true,
                }}
              />
            </Grid>
            <Grid size={3}>
              <Autocomplete
                disablePortal
                fullWidth
                disableClearable
                value={amount}
                onChange={handleAmountChange}
                renderInput={renderAmountInput}
                filterOptions={filterAmountOptions}
                getOptionLabel={getAmountOptionsLabel}
                options={INCENTIVE_BUDGET_OPTIONS}
                slotProps={{
                  listbox: {
                    style: {
                      maxHeight: 180,
                      minWidth: 100,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={5}>
              <SelectControl
                control={control}
                name="occasion"
                label={t('INCENTIVES.fields.occasion')}
                fullWidth
                margin="dense"
                rules={{ required: true, onChange: handleChangeIncentive }}
                options={INCENTIVE_OCCASION_OPTIONS.map(n => ({
                  value: n,
                  label: t(`couponsModule:giftOccasions.${n}`),
                }))}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              mb: 1,
            }}
          >
            <PlaceholderInput
              label={t('INCENTIVES.placeHolderInput.inputLabel')}
              placeholders={placeholders}
              onChange={handleChangeDescription}
              initialValue={descriptionPrefill}
            />
          </Box>
          <Box
            sx={{
              mb: 1,
            }}
          >
            {giftYearlyAvailable(occasion) && (
              <CheckboxControl
                control={control}
                name="recurring"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('INCENTIVES.fields.recurring')}
                    <Tooltip title={t('INCENTIVES.fields.recurringInfo')}>
                      <InfoOutlinedIcon />
                    </Tooltip>
                  </Box>
                }
                disabled={!giftYearlyAvailable(occasion)}
              />
            )}
            {showInviteUser && (
              <CheckboxControl control={control} name="inviteWhenActive" label={t('INCENTIVES.fields.inviteWhenActive')} />
            )}
            <CheckboxControl control={control} name="sendMailWhenActive" label={t('INCENTIVES.fields.sendMailWhenActive')} />
          </Box>

          {!isLoading && amount && (
            <Box>
              <AvailableCouponsAccordion coupons={data?.coupons} amount={amount} isIncentive />
            </Box>
          )}
        </form>
      }
      actions={
        <DialogLoadingButton
          loading={giftMutation.isPending}
          disabled={yearlyIncentivesAmount + amount > MAX_YEARLY_INCENTIVE_AMOUNT}
          onClick={handleSubmit(handleSaveGift)}
          data-test-id="button-gift-submit"
        >
          {currentGift ? t('common:buttons.save') : t('common:buttons.create')}
        </DialogLoadingButton>
      }
    />
  );
};
